// StudentCarousel.jsx – Circular/wrap-around carousel with touch swipe, arrows, keyboard.
// Smooth sliding animation: all 3 visible cards move in unison when switching.
import React, { useCallback, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import StudentCard from './StudentCard';

const ANIM_MS = 380;

const StudentCarousel = ({ students, activeIndex, onChangeIndex, progressMap, animateStageForId, flowerCriteria = [] }) => {
  const touchStartX = useRef(null);
  const containerRef = useRef(null);
  const [sliding, setSliding] = useState('idle'); // 'idle' | 'sliding-left' | 'sliding-right'
  const slidingRef = useRef(false);

  const count = students.length;
  const wrap = useCallback((index) => ((index % count) + count) % count, [count]);

  const prev = wrap(activeIndex - 1);
  const next = wrap(activeIndex + 1);

  const go = useCallback((dir) => {
    if (slidingRef.current || count < 2) return;
    slidingRef.current = true;
    setSliding(dir === 1 ? 'sliding-left' : 'sliding-right');
    setTimeout(() => {
      onChangeIndex(wrap(activeIndex + dir));
      setSliding('idle');
      slidingRef.current = false;
    }, ANIM_MS);
  }, [activeIndex, count, onChangeIndex, wrap]);

  const goNext = useCallback(() => go(1), [go]);
  const goPrev = useCallback(() => go(-1), [go]);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    dx < 0 ? goNext() : goPrev();
  };
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') goPrev();
    else if (e.key === 'ArrowRight') goNext();
  };

  if (count === 0) return null;

  const defaultProgress = { stage: 0, stageStars: 0, badgesEarned: 0, lifetimeStars: 0 };

  // Compute per-card transform based on slide state
  // During idle: prev=left, active=center, next=right
  // sliding-left:  prev slides off-left, active slides to left, next slides to center
  // sliding-right: prev slides to center, active slides to right, next slides off-right
  const getCardStyle = (role) => {
    // Base positions
    const positions = {
      prev:   { x: 'calc(-100% - 32px)', scale: 0.75, opacity: 0.5 },
      active: { x: '0px',                scale: 1.0,  opacity: 1.0 },
      next:   { x: 'calc(100% + 32px)',  scale: 0.75, opacity: 0.5 },
    };
    // Slide targets: each card moves one slot over
    const slideLeftTarget = {
      prev:   { x: 'calc(-200% - 64px)', scale: 0.6, opacity: 0 },
      active: { x: 'calc(-100% - 32px)', scale: 0.75, opacity: 0.5 },
      next:   { x: '0px',                scale: 1.0,  opacity: 1.0 },
    };
    const slideRightTarget = {
      prev:   { x: '0px',                scale: 1.0,  opacity: 1.0 },
      active: { x: 'calc(100% + 32px)',  scale: 0.75, opacity: 0.5 },
      next:   { x: 'calc(200% + 64px)',  scale: 0.6, opacity: 0 },
    };

    let target;
    if (sliding === 'sliding-left') target = slideLeftTarget[role];
    else if (sliding === 'sliding-right') target = slideRightTarget[role];
    else target = positions[role];

    const isAnimating = sliding !== 'idle';
    return {
      transform: `translate(-50%, -50%) translateX(${target.x}) scale(${target.scale})`,
      opacity: target.opacity,
      transition: isAnimating
        ? `transform ${ANIM_MS}ms cubic-bezier(0.35, 0, 0.15, 1), opacity ${ANIM_MS}ms ease`
        : 'transform 0ms, opacity 0ms',
      zIndex: role === 'active' ? 5 : 2,
    };
  };

  const renderCard = (idx, role) => {
    const s = students[idx];
    const prog = progressMap[s.id] || defaultProgress;
    const style = getCardStyle(role);
    const isActive = role === 'active' && sliding === 'idle';

    return (
      <Box
        key={s.id + '-' + role}
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: { xs: '92vw', sm: 440, md: 480 },
          maxWidth: 480,
          ...style,
          pointerEvents: isActive ? 'auto' : 'none',
          willChange: 'transform, opacity',
          // Hide neighbors on xs
          display: {
            xs: role !== 'active' ? 'none' : 'block',
            md: 'block',
          },
        }}
      >
        <StudentCard
          student={s}
          progress={prog}
          flowerCriteria={flowerCriteria}
          animateStage={role === 'active' && animateStageForId === s.id}
        />
      </Box>
    );
  };

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        outline: 'none',
        px: 1,
        minWidth: 0,
        minHeight: { xs: 360, sm: 440, md: 500 },
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Left arrow */}
      <IconButton
        onClick={goPrev}
        disabled={sliding !== 'idle'}
        sx={{
          position: 'absolute',
          left: { xs: 4, md: 24 },
          zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.9)',
          '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
          width: { xs: 48, md: 56 },
          height: { xs: 48, md: 56 },
          boxShadow: 2,
        }}
        aria-label="Önceki öğrenci"
      >
        <ChevronLeft fontSize="large" />
      </IconButton>

      {/* Cards: prev, active, next */}
      {count > 2 && renderCard(prev, 'prev')}
      {renderCard(activeIndex, 'active')}
      {count > 1 && renderCard(next, 'next')}

      {/* Right arrow */}
      <IconButton
        onClick={goNext}
        disabled={sliding !== 'idle'}
        sx={{
          position: 'absolute',
          right: { xs: 4, md: 24 },
          zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.9)',
          '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
          width: { xs: 48, md: 56 },
          height: { xs: 48, md: 56 },
          boxShadow: 2,
        }}
        aria-label="Sonraki öğrenci"
      >
        <ChevronRight fontSize="large" />
      </IconButton>
    </Box>
  );
};

export default StudentCarousel;
